import { createContext, useCallback, useContext, useState, ReactNode } from 'react'
import { Alert, Snackbar } from '@mui/material'

type Severity = 'error' | 'warning' | 'info' | 'success'

interface ToastState {
  open: boolean
  message: string
  severity: Severity
}

interface ToastContextValue {
  showError: (message: string) => void
  showSuccess: (message: string) => void
  showWarning: (message: string) => void
  showInfo: (message: string) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'error',
  })

  const show = useCallback((message: string, severity: Severity) => {
    setToast({ open: true, message, severity })
  }, [])

  const showError   = useCallback((m: string) => show(m, 'error'),   [show])
  const showSuccess = useCallback((m: string) => show(m, 'success'), [show])
  const showWarning = useCallback((m: string) => show(m, 'warning'), [show])
  const showInfo    = useCallback((m: string) => show(m, 'info'),    [show])

  const handleClose = () => setToast((prev) => ({ ...prev, open: false }))

  return (
    <ToastContext.Provider value={{ showError, showSuccess, showWarning, showInfo }}>
      {children}
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleClose} severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
